import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import logger from '../utils/logger';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add auth header to requests
  api.interceptors.request.use((config) => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      config.headers.Authorization = `Bearer ${storedToken}`;
    }
    return config;
  });

  // Handle 401 responses
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData, mfa_required, mfa_token } = response.data;
    
    // If MFA is required, return the MFA info without setting token
    if (mfa_required) {
      return { mfa_required: true, mfa_token, user: userData };
    }
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return { mfa_required: false, user: userData };
  };

  const verifyMFA = async (mfaToken, code) => {
    const response = await api.post('/auth/mfa/verify', { mfa_token: mfaToken, code });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  };

  const loginWithSSO = async (provider, data) => {
    try {
      const response = await api.post(`/sso/${provider}/session`, data);
      const { success, user: userData, session_token } = response.data;
      
      if (success && userData) {
        // Store session token in localStorage for SSO
        localStorage.setItem('sso_session', session_token);
        setUser(userData);
        return { success: true, user: userData };
      }
      
      return { success: false };
    } catch (error) {
      console.error('SSO login error:', error);
      return { success: false, error: error.response?.data?.detail || 'SSO login failed' };
    }
  };

  const logout = () => {
    // Clear ALL app-specific localStorage keys so session data doesn't leak
    // across user switches. We keep a small allow-list for user preferences
    // that should survive logout (theme, etc. — none today).
    try {
      const preserve = new Set([]);
      const keys = Object.keys(localStorage);
      keys.forEach((k) => {
        if (!preserve.has(k)) {
          try {
            localStorage.removeItem(k);
          } catch (err) {
            logger.debug('logout: could not remove localStorage key', k, err);
          }
        }
      });
    } catch (err) {
      // Fall back to explicit removals if full clear isn't possible.
      logger.debug('logout: full localStorage clear failed, falling back', err);
      try { localStorage.removeItem('token'); }
      catch (e) { logger.debug('logout: token removal failed', e); }
      try { localStorage.removeItem('sso_session'); }
      catch (e) { logger.debug('logout: sso_session removal failed', e); }
    }
    setToken(null);
    setUser(null);

    // Also call SSO logout endpoint if there was an SSO session
    api.post('/sso/logout').catch((err) => logger.debug('sso logout failed', err));
  };

  const isAdmin = user?.role === 'admin';
  const isFacilityManager = user?.role === 'facility_manager';
  const isTechnician = user?.role === 'technician';
  const isContractor = user?.role === 'contractor';

  const canManageUsers = isAdmin || isFacilityManager;
  const canManageSites = isAdmin || isFacilityManager;
  const canManageAssets = isAdmin || isFacilityManager || isTechnician;
  const canCreateWorkOrders = isAdmin || isFacilityManager || isTechnician;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        verifyMFA,
        refreshUser,
        register,
        loginWithSSO,
        logout,
        api,
        isAdmin,
        isFacilityManager,
        isTechnician,
        isContractor,
        canManageUsers,
        canManageSites,
        canManageAssets,
        canCreateWorkOrders,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
